import { resendVerificationEmail } from '@jaymathew/ui-shared/apis/CreateAccount'
import * as firebaseConfig from '@jaymathew/ui-shared/apis/FirebaseUtilities/FirebaseConfig'
import { loginAnalytics, sendPasswordResetEmail } from '@jaymathew/ui-shared/apis/User'
import { useToast } from '@jaymathew/ui-shared/contexts/ToastContext'
import { BasicDialog } from '@jaymathew/ui-shared/elements/BasicDialog'
import { Div } from '@jaymathew/ui-shared/elements/Div'
import { Login } from '@jaymathew/ui-shared/modules/login/Login'
import { ResetPasswordDialog } from '@jaymathew/ui-shared/modules/login/ResetPasswordDialog'
import { SelectCompanyDialog } from '@jaymathew/ui-shared/modules/login/SelectCompanyDialog'
import { VerificationNeededDialog } from '@jaymathew/ui-shared/modules/login/VerificationNeededDialog'
import { Paper } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'
import {
	browserSessionPersistence,
	setPersistence,
	signInWithEmailAndPassword
} from 'firebase/auth'
import { queryClient } from 'main'
import { useGetCompanies } from 'queryHooks/company/useGetCompanies'
import { useUpdateUserVerificationMutation } from 'queryHooks/users/useUpdateUserVerificationMutation'
import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { completeProdileURL, createAccountPage, logo } from 'resources/constants'
import * as types from 'resources/types'
import { Company } from 'resources/types'
import { resolveLandingPathForLogin } from 'shared/functions/resolveLandingPage'
import { useAuthBootstrap } from 'shared/hooks/useAuthBootstrap'
import { QK, fetchCurrentUser } from 'shared/hooks/useCurrentUser'
import { useSelectedCompany } from 'shared/hooks/useSelectedCompany'
import { useUserCompanyIds } from 'shared/hooks/useUserCompanyIds'
import { shouldRedirectToCompleteProfile } from 'views/completeProfile/profileRequirements'
import { useCompanyDecision } from './useCompanyDecision'

type SharedAppCheckFailureReason = 'no-recaptcha' | 'already-rendered' | 'appcheck-throttled' | 'idb-transient' | 'unknown'

type SharedAppCheckReadiness =
	| { ok: true }
	| { ok: false; reason: SharedAppCheckFailureReason; technicalCode?: string; technicalMessage?: string }

type SharedEnsureAppCheckReady = (options?: { forceRefresh?: boolean }) => Promise<SharedAppCheckReadiness>

const appCheckFailureMessages: Record<SharedAppCheckFailureReason, string> = {
	'no-recaptcha': 'Security check failed. Please reload the website and try again.',
	'already-rendered': 'Security check failed. Please reload the website and try again.',
	'appcheck-throttled': 'Security check is temporarily unavailable. Please wait a moment, reload the website, and try again.',
	'idb-transient': 'Security check failed. Please reload the website and try again.',
	'unknown': 'Security check failed. Please reload the website and try again.',
}

const auth = firebaseConfig.auth
const ensureAppCheckReadyExportName = `ensure${'AppCheckReady'}`

const getEnsureAppCheckReady = (): SharedEnsureAppCheckReady | null => {
	const maybeEnsure = (firebaseConfig as Record<string, unknown>)[ensureAppCheckReadyExportName]
	return typeof maybeEnsure === 'function' ? (maybeEnsure as SharedEnsureAppCheckReady) : null
}

const styles = {
	root: {
		backgroundColor: (theme: Theme) => theme.palette.background.default,
		minHeight: '100vh',
		display: 'flex',
		justifyContent: 'center',
		alignItems: { xs: 'stretch', sm: 'center' },
		padding: { xs: 0, sm: 2 },
	} as SxProps<Theme>,
	paper: {
		backgroundColor: 'background.paper',
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-start',
		alignItems: 'center',
		width: { xs: '100vw', sm: 460 },
	} as SxProps<Theme>,
}

interface useLocationType {
  redirectURL?: string
}

const LoginPage: FC = () => {
	const navigate = useNavigate()
	const locationState = useLocation().state as useLocationType
	const { triggerToast } = useToast()
	const decidingNavRef = useRef(false)

	const { setCurrentCompany } = useSelectedCompany()

	const [shouldShowforgotPasswordDialog, setShouldShowforgotPasswordDialog] = useState(false)
	const [errorObject, setErrorObject] = useState({ title: '', message: '', shouldShow: false })
	const [shouldShowCompanyListDialog, setShouldShowCompanyListDialog] = useState(false)
	const [localLoading, setLocalLoading] = useState(false)
	const [signInLoading, setSignInLoading] = useState(false)
	const [shouldShowAccountNeedsVerifiedDialog, setShouldShowAccountNeedsVerifiedDialog] = useState(false)

	const closeVerificationEmailDialog = () => {
		setShouldShowAccountNeedsVerifiedDialog(false)
	}

	const { user: firebaseUser } = useAuthBootstrap()

	// Claims → company IDs
	const { data: memberships, isLoading: membershipsLoading, isFetched: membershipsFetched } = useUserCompanyIds(firebaseUser?.uid)
	const companyIDs = useMemo(() => memberships?.map((m) => m.companyId) ?? [], [memberships])

	// Company objects for those IDs
	const { companies, isLoading: companiesLoading, isFetched: companiesFetched } = useGetCompanies({
		params: { companyIDs },
		overrides: { enabled: membershipsFetched && companyIDs.length > 0 }, // wait for claims
	})

	// Decide company selection once
	useCompanyDecision({
		fbUser: firebaseUser,
		memberships,
		companies,
		membershipsFetched,
		membershipsLoading,
		companiesFetched,
		companiesLoading,
		openPicker: () => setShouldShowCompanyListDialog(true),
		autoSelect: (company: Company) => {
			setCurrentCompany.mutate(company)
			navToApp(company)
		},
		onEmpty: () => {
			setErrorObject({
				title: 'No Active Company',
				message: 'We couldn\'t find any active companies attached to your profile. If you believe this is an error please contact the company you believe to be enrolled with.',
				shouldShow: true,
			})
		},
	})

	const { mutateAsync: UpdateUserVerification } = useUpdateUserVerificationMutation()

	const navCreateAccount = () => navigate(createAccountPage)
	const showforgotPasswordDialog = () => setShouldShowforgotPasswordDialog(true)

	const closeDialogs = () => {
		setShouldShowforgotPasswordDialog(false)
		setErrorObject({ title: '', message: '', shouldShow: false })
		closeVerificationEmailDialog()
	}

	const selectACompany = (company: { id: string; name: string }) => {
		const newCompany = company as types.Company
		setCurrentCompany.mutate(newCompany)
		void navToApp(newCompany)
	}

	const sendForgotPasswordEmail = async (email: string) => {
		try {
			triggerToast('Password reset email sent', 'success')
			setShouldShowforgotPasswordDialog(false)
			await sendPasswordResetEmail(email)
		} catch {
			triggerToast('There was an error sending reset email.', 'error')
		}
	}

	const sendVerificationEmail = async () => {
		triggerToast('Verification email sent', 'success')
		closeVerificationEmailDialog()
		try {
			await resendVerificationEmail()
		} catch {
			triggerToast('There was an error sending verification email', 'error')
		} finally {
			auth.signOut()
		}
	}

	const signIn = async (email: string, password: string) => {
		if (!email || !password) {
			setErrorObject({
				shouldShow: true,
				title: 'Missing Information',
				message: 'Please enter your email and password before logging in.',
			})
			return
		}

		setSignInLoading(true)
		try {
			await setPersistence(auth, browserSessionPersistence)
			const ensureSharedAppCheckReady = getEnsureAppCheckReady()
			if (!ensureSharedAppCheckReady) {
				setErrorObject({
					shouldShow: true,
					title: 'Security Check Failed',
					message: appCheckFailureMessages.unknown,
				})
				return
			}

			const appCheckReady = await ensureSharedAppCheckReady()
			if (!appCheckReady.ok) {
				setErrorObject({
					shouldShow: true,
					title: 'Security Check Failed',
					message: appCheckFailureMessages[appCheckReady.reason] || appCheckFailureMessages.unknown,
				})
				return
			}
			await signInWithEmailAndPassword(auth, email, password)
			// no need to set local firebaseUser if you use useAuthBootstrap()
		} catch (error) {
			const code = (error as { code?: string }).code
			const message = (error as Error).message
			let friendlyMessage = message
			if (code === 'auth/wrong-password') friendlyMessage = 'The username and/or password does not match our records.'
			else if (code === 'auth/user-not-found') friendlyMessage = 'The username does not match our records.'
			else if (code === 'auth/invalid-credential') friendlyMessage = 'The username and/or password does not match our records.'
			setErrorObject({ shouldShow: true, title: 'Error Signing In', message: friendlyMessage })
		} finally {
			setSignInLoading(false)
		}
	}

	const navToApp = useCallback(
		async (company: types.Company) => {
			if (decidingNavRef.current) return  // ⬅️ prevents running twice
			decidingNavRef.current = true
			try {
				setLocalLoading(true)
				setShouldShowCompanyListDialog(false)

				// Ensure current user is loaded before routing
				const user = await queryClient.ensureQueryData({
					queryKey: QK(company.id),
					queryFn: () => fetchCurrentUser(company.id),
				})

				if (!user.verified) {
					await Promise.all([
						UpdateUserVerification(company.id),
						loginAnalytics(company.id),
					]).catch(() => {})
				} else {
					await loginAnalytics(company.id).catch(() => {})
				}

				const { shouldRedirect } = shouldRedirectToCompleteProfile(user, company)
				if (shouldRedirect) {
					navigate(completeProdileURL, { state: { redirectURL: locationState?.redirectURL } })
				} else if (locationState?.redirectURL) {
					navigate(locationState.redirectURL)
				} else {
					const landingPage = resolveLandingPathForLogin(user, company)
					navigate(landingPage)
				}
			} catch (error) {
				if (error instanceof Error) {
					setErrorObject({ shouldShow: true, message: error.message, title: 'Error Retrieving Data' })
				}
			} finally {
				setLocalLoading(false)
				decidingNavRef.current = false
			}
		},
		[UpdateUserVerification, navigate, locationState?.redirectURL],
	)

	return (
		<Div sx={styles.root}>
			<Paper elevation={3} sx={styles.paper}>
				<Login
					logo={logo}
					loading={signInLoading || localLoading}
					showforgotPasswordDialog={showforgotPasswordDialog}
					navCreateAccount={navCreateAccount}
					signIn={signIn}
				/>
			</Paper>

			{shouldShowCompanyListDialog && (
				<SelectCompanyDialog
					selectedCompany={selectACompany}
					arrayOfCompanies={companies}
					defaultCompany={(companies?.[0] as types.Company) ?? undefined}
				/>
			)}

			{shouldShowforgotPasswordDialog && (
				<ResetPasswordDialog closeDialogs={closeDialogs} sendForgotPasswordEmail={sendForgotPasswordEmail} />
			)}

			{shouldShowAccountNeedsVerifiedDialog && (
				<VerificationNeededDialog closeDialogs={closeDialogs} sendVerificationEmail={sendVerificationEmail} />
			)}

			{errorObject.shouldShow && (
				<BasicDialog
					shouldShow={errorObject.shouldShow}
					title={errorObject.title}
					message={errorObject.message}
					closeDialog={closeDialogs}
				/>
			)}
		</Div>
	)
}

export default LoginPage
